<template>
  <section
    id="content"
    class="relative flex gap-x-4 md:pt-15"
    :class="
      isCaseStudy
        ? 'flex-col'
        : 'flex-col md:flex-row border-b border-[#CECED1]'
    "
  >
    <aside
      v-if="!isCaseStudy"
      class="self-start md:w-[10%] mt-3 mb-4 flex flex-row gap-4 md:flex-col md:sticky md:float-left md:top-[100px] md:left-0"
    >
      <span class="text-base self-center md:self-auto text-bw-900">Share</span>
      <button @click="copyLink">
        <NuxtIcon name="copy-link" filled class="share-icon cursor-pointer" />
      </button>
      <CommonSocialShare :social-media="socMedList" />
    </aside>

    <div class="detail-content text-base w-full md:w-2/3 mt-3 pb-8 md:pb-10">
      <div
        class="mb-6 text-base content"
        v-html="detailData.layout_component?.[0]?.content"
      ></div>

      <div
        v-if="isCaseStudy || isIdeasArticle"
        class="tags mt-10 text-bw-900 flex flex-col gap-6"
      >
        <div
          class="flex gap-3"
          :class="{
            'flex-col md:flex-row md:items-center': isIdeasArticle,
            'flex-col': isCaseStudy
          }"
        >
          <h4 class="text-base font-medium">Industry</h4>
          <div class="flex flex-wrap gap-3">
            <NuxtLink
              v-if="isCaseStudy && detailData.study_case_client?.data"
              :to="`/industries/${detailData.study_case_client?.data?.attributes?.industry?.data?.attributes?.slug}`"
              :title="
                detailData.study_case_client?.data?.attributes?.industry?.data
                  ?.attributes?.title
              "
              class="text-sm p-2 border border-[#CECED1] rounded-md hover:border-primary-primary hover:bg-primary-primary hover:text-white"
            >
              {{
                detailData.study_case_client?.data?.attributes?.industry?.data
                  ?.attributes?.title
              }}
            </NuxtLink>
            <template
              v-if="isIdeasArticle && detailData.article_industries?.data"
            >
              <NuxtLink
                v-for="industry in detailData.article_industries?.data"
                :key="industry.id"
                :to="`/industries/${industry.attributes?.slug}`"
                :title="industry.attributes?.title"
                class="text-sm p-2 border border-[#CECED1] rounded-md hover:border-primary-primary hover:bg-primary-primary hover:text-white"
              >
                {{ industry.attributes?.title }}
              </NuxtLink>
            </template>
          </div>
        </div>
        <div
          class="flex gap-3"
          :class="{
            'flex-col md:flex-row md:items-center': isIdeasArticle,
            'flex-col': isCaseStudy
          }"
        >
          <h4 class="text-base font-medium">Expertises</h4>
          <div class="flex flex-wrap gap-3">
            <NuxtLink
              v-for="expertise in detailData.expertices.data"
              :key="expertise.id"
              :to="`/expertises/${expertise.attributes?.slug}`"
              :title="expertise.attributes.title"
              class="text-sm p-2 border border-[#CECED1] rounded-md hover:border-primary-primary hover:bg-primary-primary hover:text-white"
            >
              {{ expertise.attributes.title }}
            </NuxtLink>
          </div>
        </div>
        <div
          v-if="
            isCaseStudy &&
            detailData.study_case_client.data?.attributes?.client_types?.data
              .length !== 0
          "
          class="flex flex-col gap-3"
        >
          <h4 class="text-base font-medium">Organization Type</h4>
          <div class="flex flex-wrap gap-3">
            <button
              v-for="client in detailData?.study_case_client?.data?.attributes
                ?.client_types.data"
              :key="client.id"
              :title="client.attributes.type"
              class="text-sm p-2 border border-[#CECED1] rounded-md hover:text-white hover:bg-primary-primary hover:border-primary-primary"
              @click.prevent="redirectToCaseStudies(client.attributes.type)"
            >
              {{ client.attributes.type }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
// VARIABLE DECLARATION
const props = defineProps({
  detailData: {
    type: Object,
    required: true
  },
  isCaseStudy: {
    type: Boolean,
    default: false
  },
  isIdeasArticle: {
    type: Boolean,
    default: false
  }
})

const router = useRouter()

const currentUrl = () => {
  let locHref = ''
  if (process.client) {
    locHref = window.location.href
  }
  return locHref
}

const socMedList = ref([
  {
    name: 'whatsapp',
    title: props.detailData.title,
    url: currentUrl(),
    description: props.detailData.layout_component?.[0]?.content
  },
  {
    name: 'linkedin',
    title: props.detailData.title,
    url: currentUrl(),
    description: props.detailData.layout_component?.[0]?.content
  }
])

const redirectToCaseStudies = type => {
  router.push({ name: 'work', query: { org_type: type } })
}

const copyLink = () => {
  const el = document.createElement('textarea')
  el.value = currentUrl()
  el.setAttribute('readonly', '')
  el.style.position = 'absolute'
  el.style.left = '-9999px'
  document.body.appendChild(el)
  el.select()
  document.execCommand('copy')
  document.body.removeChild(el)
  const tooltip = document.querySelectorAll('.tooltiptext')
  Array.prototype.forEach.call(tooltip, function (element) {
    element.innerHTML = 'Copied'
  })
  setTimeout(function () {
    Array.prototype.forEach.call(tooltip, function (element) {
      element.innerHTML = 'Copy link'
    })
  }, 2000)
}
</script>

<style lang="scss" scoped>
:deep(.share-icon) {
  svg {
    width: 2.5rem;
    height: 2.5rem;
    transition: all 0.2s ease-in-out;

    &:hover {
      transform: scale(1.25);
    }
  }
}
:deep(.content) {
  strong {
    font-weight: 500 !important;
  }

  h1,
  h2 {
    font-size: 36px;
    margin-top: 24px;
    margin-bottom: 24px;
    line-height: 44px;
    font-weight: 500 !important;
    color: #353535;
  }

  h3 {
    font-size: 32px;
    margin-top: 24px;
    margin-bottom: 24px;
    line-height: 40px;
    font-weight: 500 !important;
    color: #353535;
  }

  h4 {
    font-size: 24px;
    margin-top: 24px;
    margin-bottom: 24px;
    line-height: 32px;
    font-weight: 500 !important;
    color: #353535;
  }

  h5 {
    font-size: 18px;
    margin-top: 24px;
    margin-bottom: 24px;
    line-height: 26px;
    font-weight: 500 !important;
    color: #353535;
  }

  h6 {
    font-size: 16px;
    margin-top: 24px;
    margin-bottom: 24px;
    line-height: 24px;
    font-weight: 500 !important;
    color: #353535;
  }

  p {
    margin-bottom: 1rem;
    font-size: 1rem;
    line-height: 2;
    color: #353535;
  }

  a {
    color: #ff6700;
    word-wrap: break-word;

    &:hover {
      text-decoration: underline;
    }
  }

  ol {
    list-style: decimal;
    margin-left: 32px;
    margin-bottom: 20px;

    li {
      margin-bottom: 1rem;
      color: #353535;
    }
  }

  ul {
    list-style-type: disc;
    margin-left: 32px;
    margin-bottom: 20px;

    li {
      margin-bottom: 1rem;
      color: #353535;
    }
  }

  table {
    @apply table-auto border border-bw-900 border-separate mb-4;

    th {
      @apply border border-bw-900 p-2 text-bw-900;
    }
    td {
      @apply border border-bw-900 p-2 text-bw-900;
    }
  }

  figure,
  iframe {
    @apply py-2;
  }
}
</style>
